import { useState, useEffect, useRef } from "react";
import { API } from "@/lib/api";
import { MapPin, Loader2 } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

export function AddressAutocomplete({ value, onChange, testId = "address-input" }) {
  const { t } = useLanguage();
  const [query, setQuery] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const handleInput = (v) => {
    setQuery(v);
    onChange(v);
    if (timer.current) clearTimeout(timer.current);
    if (v.trim().length < 3) {
      setResults([]);
      setOpen(false);
      return;
    }
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API}/address/autocomplete?q=${encodeURIComponent(v)}`);
        const data = await res.json();
        setResults(data.results || []);
        setOpen(true);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 450);
  };

  const pick = (label) => {
    setQuery(label);
    onChange(label);
    setResults([]);
    setOpen(false);
  };

  return (
    <div className="relative" ref={boxRef}>
      <div className="relative">
        <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-primary/60" />
        <input
          data-testid={testId}
          value={query}
          onChange={(e) => handleInput(e.target.value)}
          onFocus={() => results.length && setOpen(true)}
          placeholder={t("address_ph")}
          className="w-full h-14 pl-12 pr-11 rounded-2xl border border-input bg-white text-base focus:ring-2 focus:ring-primary focus:outline-none"
        />
        {loading && <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 animate-spin text-primary/60" />}
      </div>
      {open && results.length > 0 && (
        <ul
          data-testid="address-suggestions"
          className="absolute z-30 mt-2 w-full max-h-64 overflow-auto rounded-2xl border border-border bg-white shadow-lg"
        >
          {results.map((r, i) => (
            <li key={i}>
              <button
                type="button"
                data-testid={`address-suggestion-${i}`}
                onClick={() => pick(r.label)}
                className="flex w-full items-start gap-2 px-4 py-3 text-left text-sm hover:bg-secondary transition-colors"
              >
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-primary/60" />
                <span>{r.label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
